package it.neckar.lizergy.model.configuration.quote.economics

import it.neckar.lizergy.model.configuration.components.Einspeiseart
import it.neckar.lizergy.model.energy.PowerRating
import it.neckar.lizergy.model.energy.economics.PricePerKWh
import it.neckar.open.kotlin.lang.percent
import it.neckar.open.unit.other.kWp
import it.neckar.open.unit.other.pct

/**
 * Contains the guaranteed feed in prices
 */
object FeedInPrices {
  /**
   * The feed in prices
   *
   * Values from
   * 2023: https://www.verbraucherzentrale.de/wissen/energie/erneuerbare-energien/eeg-2023-das-hat-sich-fuer-photovoltaikanlagen-geaendert-75401
   * 2025: https://youtrack.neckar.it/agiles/106-47/current?issue=PVP-72
   * Feb 2026: https://youtrack.neckar.it/issue/PVP-92
   */
  /**
   * "Dachanlagen bis 10 kWp"
   */
  val upTo10kWPTeileinspeisung: PricePerKWh = PricePerKWh.cents(7.78)
  val upTo10kWPVolleinspeisung: PricePerKWh = PricePerKWh.cents(12.34)

  /**
   * "Dachanlagen 10 bis 40 kWp"
   */
  val upTo40kWPTeileinspeisung: PricePerKWh = PricePerKWh.cents(6.73)
  val upTo40kWPVolleinspeisung: PricePerKWh = PricePerKWh.cents(10.35)

  /**
   * "Dachanlagen über 40 kWP"
   */
  val over40kWPTeileinspeisung: PricePerKWh = PricePerKWh.cents(5.50)
  val over40kWPVolleinspeisung: PricePerKWh = PricePerKWh.cents(10.35)


  val tenKWp: PowerRating = PowerRating.kiloWattPeak(10.0)
  val fortyKWp: PowerRating = PowerRating.kiloWattPeak(40.0)

  /**
   * Returns the feed in prices for the given kWP
   */
  operator fun get(powerRating: @kWp PowerRating, einspeiseart: Einspeiseart): PricePerKWh {
    if (powerRating == PowerRating.Zero) return when (einspeiseart) {
      //Does this make sense? Why should a system with 0 kWp have a feed-in price?
      Einspeiseart.Ueberschuss -> upTo10kWPTeileinspeisung
      Einspeiseart.Volleinspeisung -> upTo10kWPVolleinspeisung
    }

    val priceForTheFirst10kWp = when (einspeiseart) {
      Einspeiseart.Ueberschuss -> upTo10kWPTeileinspeisung
      Einspeiseart.Volleinspeisung -> upTo10kWPVolleinspeisung
    }

    val priceFor10To40kWp = when (einspeiseart) {
      Einspeiseart.Ueberschuss -> upTo40kWPTeileinspeisung
      Einspeiseart.Volleinspeisung -> upTo40kWPVolleinspeisung
    }

    val priceForTheRest = when (einspeiseart) {
      Einspeiseart.Ueberschuss -> over40kWPTeileinspeisung
      Einspeiseart.Volleinspeisung -> over40kWPVolleinspeisung
    }

    val percentageOfFirst10KWp: @pct Double = powerRating.coerceIn(PowerRating.Zero, tenKWp) / powerRating
    val percentageOf10To40KWp: @pct Double = (powerRating - tenKWp).coerceIn(PowerRating.Zero, fortyKWp - tenKWp) / powerRating
    val percentageOfTheRest: @pct Double = (powerRating - fortyKWp).coerceAtLeast(PowerRating.Zero) / powerRating

    check(percentageOfFirst10KWp + percentageOf10To40KWp + percentageOfTheRest == 100.percent) { "The sum of the percentages must be 100%" }
    return priceForTheFirst10kWp * percentageOfFirst10KWp + priceFor10To40kWp * percentageOf10To40KWp + priceForTheRest * percentageOfTheRest
  }
}
